import React, { useEffect, useRef, useState } from 'react'
import { Settings, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from 'lib/utils'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { FIGMA_COLUMN_2, FIGMA_KANBAN_COLUMN } from './figmaKanbanTheme'
import { FigmaKanbanColumnShell } from './FigmaKanbanColumnShell'

interface FigmaKanbanShortlistColumnProps {
  count: number
  children: React.ReactNode
  setDroppableRef: (node: HTMLElement | null) => void
  isOver?: boolean
  minimumScore?: number
  onMinimumScoreChange?: (score: number) => void
  initialMatchingMode?: 'score' | 'top_k'
  topK?: number
}

/** Second hiring step (order 2) — display label Shortlist IA. */
export function FigmaKanbanShortlistColumn({
  count,
  children,
  setDroppableRef,
  isOver = false,
  minimumScore = 70,
  onMinimumScoreChange,
  initialMatchingMode,
  topK,
}: FigmaKanbanShortlistColumnProps) {
  const { t } = useTranslation()
  const displayTitle = t('kanban.figmaShortlistColumn', 'Shortlist IA')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [localScore, setLocalScore] = useState(String(Math.floor(minimumScore)))
  const settingsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLocalScore(String(Math.floor(minimumScore)))
  }, [minimumScore])

  useEffect(() => {
    if (!settingsOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setSettingsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [settingsOpen])

  const handleApplyScore = () => {
    if (!onMinimumScoreChange) return
    const parsed = parseInt(localScore, 10)
    if (!isNaN(parsed)) {
      onMinimumScoreChange(Math.max(0, Math.min(100, parsed)))
      setSettingsOpen(false)
    }
  }

  const hasScoreChanged = Number(localScore) !== minimumScore

  const headerActions = onMinimumScoreChange ? (
    <div className="relative shrink-0" ref={settingsRef}>
      <button
        type="button"
        onClick={() => setSettingsOpen((open) => !open)}
        className="flex h-6 w-6 items-center justify-center text-[#232323] transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#19A6A6]/40 dark:text-foreground"
        aria-label={t('kanban.figmaShortlistSettings', 'AI score settings')}
        aria-expanded={settingsOpen}
        title={t('kanban.figmaShortlistSettings', 'AI score settings')}
      >
        <Settings className="h-6 w-6" strokeWidth={1.5} />
      </button>
      {settingsOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 flex items-center gap-1 rounded-md border border-[#D8D8D8] bg-white p-2 shadow-lg dark:border-border dark:bg-card">
          <span className="text-xs font-bold text-[#00C0E8]">%</span>
          <Input
            type="number"
            min={1}
            max={100}
            step={1}
            value={localScore}
            onChange={(e) => setLocalScore(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
                e.preventDefault()
              }
            }}
            className="h-7 w-14 px-1 text-center text-xs"
          />
          {hasScoreChanged && (
            <Button
              type="button"
              size="sm"
              onClick={handleApplyScore}
              className="h-7 w-7 p-0"
              title={t('common.apply', 'Apply')}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  ) : null

  const showTopKFirstMatchNote = initialMatchingMode === 'top_k'

  const footer = (
    <div
      className="flex w-full shrink-0 flex-col items-center justify-center border-t border-[#D8D8D8] bg-[#F2F2F7] dark:border-border dark:bg-[#1F2430]"
      style={{
        minHeight: showTopKFirstMatchNote ? 52 : FIGMA_KANBAN_COLUMN.footerHeight,
        paddingTop: FIGMA_KANBAN_COLUMN.footerPaddingY,
        paddingBottom: FIGMA_KANBAN_COLUMN.footerPaddingY,
        paddingLeft: FIGMA_KANBAN_COLUMN.footerPaddingX,
        paddingRight: FIGMA_KANBAN_COLUMN.footerPaddingX,
        gap: 2,
      }}
    >
      <p className={cn('w-full text-center', FIGMA_COLUMN_2.footerTextClass)}>
        {t('kanban.figmaAiScoreFooter', 'Score IA : {{score}}%', { score: Math.floor(minimumScore) })}
      </p>
      {showTopKFirstMatchNote && (
        <p className="w-full text-center text-[8px] leading-[1.1] font-normal text-[#888888] dark:text-muted-foreground/80">
          {t(
            'kanban.figmaTopKFirstMatchNote',
            'Mode Top-K utilisé pour le premier matching{{kSuffix}}',
            {
              kSuffix: topK ? ` (${topK})` : '',
            }
          )}
        </p>
      )}
    </div>
  )

  return (
    <FigmaKanbanColumnShell
      title={displayTitle}
      count={count}
      theme={FIGMA_COLUMN_2}
      setDroppableRef={setDroppableRef}
      isOver={isOver}
      headerActions={headerActions}
      footer={footer}
    >
      {children}
    </FigmaKanbanColumnShell>
  )
}
